import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HeartPulse,
  Sprout,
  Sun,
  GraduationCap,
  Globe2,
  Network,
  Target,
  ArrowRight,
} from "lucide-react";
import PageBanner from "../components/PageBanner";

const PILLARS = [
  {
    icon: HeartPulse,
    title: "Health Access & System Innovation",
    text: "Expanding access to care through technology-enabled, community-based solutions focused on prevention, early detection and improved outcomes.",
  },
  {
    icon: Sprout,
    title: "Food Safety & Community Wellness",
    text: "Supporting systems that improve food safety, supply chain reliability and nutritional awareness tied to long-term community health.",
  },
  {
    icon: Sun,
    title: "Sustainable Energy & Infrastructure",
    text: "Deploying clean-energy solutions, including solar-powered systems, to support critical services such as healthcare and education.",
  },
  {
    icon: GraduationCap,
    title: "Future-Ready Education & Workforce",
    text: "Preparing individuals for participation in modern economies through training in technology, solar energy and emerging skill sets.",
  },
  {
    icon: Globe2,
    title: "Global Diaspora Connectivity",
    text: "Connecting people, capital and opportunity across borders — enabling investment and collaboration between global communities.",
  },
  {
    icon: Network,
    title: "Digital Trust Infrastructure",
    text: "Real-time service tracking, secure portable records and blockchain-backed transparency for funds, food chains and cross-border programs.",
  },
];

const PROGRAMS = [
  {
    title: "Health Desert Transformation Initiative",
    text: "A comprehensive model to improve access to healthcare in underserved regions through technology, partnerships and community deployment.",
  },
  {
    title: "Smart Community Hub",
    text: "An integrated solution combining solar energy, health access and education in a single community-based infrastructure.",
  },
  {
    title: "Global Food Safety Network",
    text: "Programs designed to improve food safety awareness, monitoring and distribution systems.",
  },
  {
    title: "Future Skills Academy",
    text: "Training initiatives focused on solar technology, digital skills and workforce readiness.",
  },
  {
    title: "Diaspora Investment Bridge",
    text: "A platform connecting diaspora communities to development opportunities across regions.",
  },
];

const PARTNERS = [
  "Governments",
  "Healthcare providers",
  "Energy developers",
  "Educational institutions",
  "Private sector innovators",
  "Diaspora leaders",
];

export default function Mission() {
  return (
    <div data-testid="mission-page">
      <PageBanner
        overline="Our Mission"
        title="Expanding access. Strengthening systems."
        subtitle="We design and deliver scalable solutions at the intersection of healthcare, energy, food systems, education and digital infrastructure — for communities that need them most."
        image="https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/f02eddfafe86880281dfe4f20e09317e1126a873b882438f425ee735f4fc5ede.png"
      />

      {/* Mission statement */}
      <section className="section">
        <div className="container-tmb grid md:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-5 h-5 text-[#C85A3A]" />
              <div className="overline">Mission statement</div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Expanding access to the essential systems that improve quality of life.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7 space-y-5 text-[#4A5D54] text-base md:text-lg leading-relaxed"
          >
            <p>
              TMB Global Foundation builds connected ecosystems rather than isolated
              programs. We integrate health access, clean and sustainable energy, safe
              and reliable food systems, education and workforce development, and data
              and digital infrastructure into a single coordinated approach.
            </p>
            <p>
              This integration ensures that communities move beyond temporary support
              toward long-term resilience, economic participation and independence —
              with measurable outcomes and trusted accountability at every step.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core pillars */}
      <section className="section bg-[#F0EBE1]">
        <div className="container-tmb">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">Core pillars</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Six pillars working as one connected system.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                  className="card-tmb p-8"
                  data-testid={`mission-pillar-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#C85A3A]" />
                  </div>
                  <h3 className="font-display text-2xl text-[#1A2421] mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#4A5D54] leading-relaxed">{p.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section">
        <div className="container-tmb">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">Programs</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              How our mission lives in the world.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PROGRAMS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="card-tmb p-8"
                data-testid={`mission-program-${i}`}
              >
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#C85A3A] mb-3">
                  Program 0{i + 1}
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#1A2421] mb-3 leading-snug">
                  {p.title}
                </h3>
                <p className="text-[#4A5D54] leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation & blockchain */}
      <section className="section bg-[#2C4C3B] text-white">
        <div className="container-tmb grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="text-[#D99A29] uppercase tracking-[0.2em] font-bold text-xs mb-4">
              Innovation & Digital Infrastructure
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-6">
              Technology that earns trust.
            </h2>
            <p className="text-white/85 leading-relaxed mb-4">
              We leverage digital infrastructure to enhance transparency, accountability
              and efficiency across every program.
            </p>
            <ul className="space-y-3 text-white/85">
              {[
                "Real-time service tracking",
                "Data-informed decision making",
                "Secure and portable records",
                "Cross-border connectivity systems",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="text-[#D99A29] mt-1">·</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <div className="text-[#D99A29] uppercase tracking-[0.2em] font-bold text-xs mb-4">
              Blockchain Positioning
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-6">
              Digital trust, built in.
            </h2>
            <p className="text-white/85 leading-relaxed mb-4">
              We incorporate blockchain technology as a digital trust infrastructure to
              support global development. Practical, scalable use cases include:
            </p>
            <ul className="space-y-3 text-white/85">
              {[
                "Secure health record management",
                "Transparent tracking of services and funding",
                "Food supply chain verification",
                "Cross-border investment tracking",
                "Community-based project funding",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="text-[#D99A29] mt-1">·</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section">
        <div className="container-tmb text-center max-w-3xl mx-auto">
          <div className="overline mb-4">Partnerships</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05] mb-6">
            We scale by collaborating across sectors.
          </h2>
          <p className="text-[#4A5D54] text-base md:text-lg leading-relaxed mb-10">
            Our work depends on coordinated partnerships that strengthen communities
            globally. We collaborate with:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {PARTNERS.map((p) => (
              <span
                key={p}
                className="px-5 py-2.5 rounded-full bg-[#F0EBE1] text-[#1A2421] font-semibold text-sm border border-[#C85A3A]/15"
              >
                {p}
              </span>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/donate" data-testid="mission-donate-cta" className="btn-primary">
              Fund the mission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
